import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  FormControlLabel,
  Switch,
} from "@mui/material";

import {
  FaUser,
  FaUsers,
} from "react-icons/fa";

import { Edit, Delete, Security } from "@mui/icons-material";

import KuyumcuLeftSideNavbar from "./components/KuyumcuLeftSideNavbar";
import PremiumBackground from "./components/PremiumBackground";

import ProsetDataGrid from "./components/KuyumcuDataGrid";
import ProsetPopup from "./components/ProsetPopup";
import SilmeUyariPopup from "./components/SilmeUyariPopup";

import axiosInstance from "../../api/axiosInstance";
import { getUserId } from "../../utils/getUserId";
import { usePermission } from "../../context/PermissionContext";

import "./css/KuyumcuMainPage.css";
import "./css/KuyumcuKullanicilarPage.css";

const uyelikTipSecenekleri = [
  { value: "PERSONEL", label: "PERSONEL" },
  { value: "ORTAK", label: "ORTAK" },
  { value: "CANTACI", label: "CANTACI" },
];

export default function KuyumcuKullanicilarPage() {
  const navigate = useNavigate();

  const girisYapanUserId = getUserId();
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  const { can, isLoading } = usePermission();

  /* =========================
     YETKİ KODLARI
  ========================= */
  const PERM_KULLANICI_VIEW = "KULLANICILAR_VIEW";
  const PERM_KULLANICI_CREATE = "KULLANICILAR_CREATE";
  const PERM_KULLANICI_UPDATE = "KULLANICILAR_UPDATE";
  const PERM_KULLANICI_DELETE = "KULLANICILAR_DELETE";

  const canView = !isLoading && can(PERM_KULLANICI_VIEW);
  const canCreate = !isLoading && can(PERM_KULLANICI_CREATE);
  const canUpdate = !isLoading && can(PERM_KULLANICI_UPDATE);
  const canDelete = !isLoading && can(PERM_KULLANICI_DELETE);

  /* =========================
     SAYFA ERİŞİM KONTROLÜ (LOGIN)
  ========================= */
  useEffect(() => {
    if (!girisYapanUserId || !token) {
      navigate("/login");
      return;
    }
  }, [girisYapanUserId, token, navigate]);

  /* =========================
     STATE
  ========================= */
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [rows, setRows] = useState([]);

  // Popup
  const [popupOpen, setPopupOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Silme popup
  const [silmePopupOpen, setSilmePopupOpen] = useState(false);
  const [silinecekId, setSilinecekId] = useState(null);
  const [silmeMesaji, setSilmeMesaji] = useState(
    "Bu kullanıcıyı silmek istediğinize emin misiniz?"
  );

  // Form
  const [form, setForm] = useState({
    kullaniciAdi: "",
    isim: "",
    soyisim: "",
    password: "",
    mail: "",
    telNo: "",
    uyelikTip: "PERSONEL",
    aktifUyeMi: true,
  });

  const resetForm = () => {
    setForm({
      kullaniciAdi: "",
      isim: "",
      soyisim: "",
      password: "",
      mail: "",
      telNo: "",
      uyelikTip: "PERSONEL",
      aktifUyeMi: true,
    });
    setEditMode(false);
    setEditingId(null);
  };

  /* =========================
     API
  ========================= */
  const listele = async () => {
    // Yetkiler yükleniyorsa bekle
    if (isLoading) return;

    // VIEW yoksa backend'e çağrı atma
    if (!can(PERM_KULLANICI_VIEW)) {
      setRows([]);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const resp = await axiosInstance.get("/kuyumcu-kullanicilar", {
        params: { girisYapanUserId },
      });
      setRows(resp.data || []);
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.response?.data ||
          "Kullanıcılar getirilemedi."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (girisYapanUserId) listele();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [girisYapanUserId, isLoading]);

  const kaydet = async () => {
    setError("");

    if (isLoading) return;

    // CREATE/UPDATE permission guard
    const needPerm = editMode ? PERM_KULLANICI_UPDATE : PERM_KULLANICI_CREATE;
    if (!can(needPerm)) {
      setError("Bu işlem için yetkiniz yok.");
      return;
    }

    // Basit validasyon
    if (!form.kullaniciAdi || form.kullaniciAdi.trim().length < 3) {
      setError("Kullanıcı adı en az 3 karakter olmalı.");
      return;
    }
    if (!editMode) {
      if (!form.password || form.password.trim().length < 8) {
        setError("Şifre en az 8 karakter olmalı.");
        return;
      }
    }

    try {
      if (!editMode) {
        // CREATE
        await axiosInstance.post(
          "/kuyumcu-kullanicilar",
          {
            kullaniciAdi: form.kullaniciAdi,
            isim: form.isim,
            soyisim: form.soyisim,
            password: form.password,
            mail: form.mail,
            telNo: form.telNo,
            uyelikTip: form.uyelikTip,
          },
          { params: { girisYapanUserId } }
        );
      } else {
        // UPDATE (password boş gelirse değiştirme)
        await axiosInstance.patch(
          `/kuyumcu-kullanicilar/${editingId}`,
          {
            isim: form.isim,
            soyisim: form.soyisim,
            mail: form.mail,
            telNo: form.telNo,
            uyelikTip: form.uyelikTip,
            aktifUyeMi: form.aktifUyeMi,
            password: form.password || "",
          },
          { params: { girisYapanUserId } }
        );
      }

      setPopupOpen(false);
      resetForm();
      listele();
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.response?.data ||
          "Kayıt işlemi başarısız."
      );
    }
  };

  const duzenleAc = (row) => {
    if (isLoading) return;

    if (!can(PERM_KULLANICI_UPDATE)) {
      setError("Düzenleme yetkiniz yok.");
      return;
    }

    setEditMode(true);
    setEditingId(row.id);

    setForm({
      kullaniciAdi: row.kullaniciAdi || "",
      isim: row.isim || "",
      soyisim: row.soyisim || "",
      password: "", // güvenlik: boş gelsin, kullanıcı isterse yeni şifre girsin
      mail: row.mail || "",
      telNo: row.telNo || "",
      uyelikTip: row.uyelikTip || "PERSONEL",
      aktifUyeMi: row.aktifUyeMi ?? true,
    });

    setPopupOpen(true);
  };

  const silAc = (row) => {
    if (isLoading) return;

    if (!can(PERM_KULLANICI_DELETE)) {
      setError("Silme yetkiniz yok.");
      return;
    }

    setSilinecekId(row.id);
    setSilmeMesaji(
      `“${row.kullaniciAdi}” kullanıcısını silmek istediğinize emin misiniz?`
    );
    setSilmePopupOpen(true);
  };

  const silOnayla = async () => {
    if (isLoading) return;
    if (!silinecekId) return;

    if (!can(PERM_KULLANICI_DELETE)) {
      setError("Silme yetkiniz yok.");
      setSilmePopupOpen(false);
      setSilinecekId(null);
      return;
    }

    try {
      await axiosInstance.delete(`/kuyumcu-kullanicilar/${silinecekId}`, {
        params: { girisYapanUserId },
      });
      setSilmePopupOpen(false);
      setSilinecekId(null);
      listele();
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.response?.data ||
          "Silme işlemi başarısız."
      );
    }
  };

  /* =========================
     GRID
  ========================= */
  const columns = useMemo(() => {
    const cols = [];

    // Yetkiler kolonunu sadece yetkisi olana göster
    if (canUpdate) {
      cols.push({
        field: "yetkiler",
        headerName: "Yetkiler",
        width: 140,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (p) => (
          <Button
            variant="outlined"
            size="small"
            startIcon={<Security fontSize="small" />}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/kuyumcuKullaniciYetkiler/${p.row.id}`, {
                state: { kullaniciAdi: p.row.kullaniciAdi },
              });
            }}
            sx={{
              borderColor: "#b8860b",
              color: "#b8860b",
              fontWeight: 800,
              borderRadius: "10px",
              textTransform: "none",
              "&:hover": { borderColor: "#b8860b" },
            }}
          >
            Yetkiler
          </Button>
        ),
      });
    }

    cols.push(
      { field: "kullaniciAdi", headerName: "Kullanıcı Adı", flex: 1, minWidth: 160 },

      // İSTENEN YENİ ALANLAR (kullanıcı adından hemen sonra)
      { field: "isim", headerName: "İsim", flex: 1, minWidth: 140 },
      { field: "soyisim", headerName: "Soyisim", flex: 1, minWidth: 140 },

      { field: "uyelikTip", headerName: "Rol", width: 140 },
      { field: "mail", headerName: "Mail", flex: 1, minWidth: 200 },
      { field: "telNo", headerName: "Telefon", width: 140 },
      {
        field: "aktifUyeMi",
        headerName: "Aktif",
        width: 110,
        renderCell: (p) => (
          <span
            className={p.value ? "kk-aktif-badge kk-aktif" : "kk-aktif-badge kk-pasif"}
          >
            {p.value ? "AKTİF" : "PASİF"}
          </span>
        ),
      },
      {
        field: "lisansGecerlilikTarihi",
        headerName: "Geçerlilik Tarih",
        width: 130,
        sortable: false,
        renderCell: (p) => {
          const v = p?.row?.lisansGecerlilikTarihi;
          if (!v) return "";

          if (typeof v === "string" && v.includes("-")) {
            const [y, m, d] = v.split("-");
            if (y && m && d) return `${d}.${m}.${y}`;
            return v;
          }

          return String(v);
        },
      }
    );

    // İşlemler kolonu: update/delete yoksa hiç gösterme
    if (canUpdate || canDelete) {
      cols.push({
        field: "islemler",
        headerName: "İşlemler",
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {canUpdate && (
              <IconButton size="small" onClick={() => duzenleAc(p.row)}>
                <Edit fontSize="small" />
              </IconButton>
            )}
            {canDelete && (
              <IconButton size="small" onClick={() => silAc(p.row)}>
                <Delete fontSize="small" />
              </IconButton>
            )}
          </Box>
        ),
      });
    }

    return cols;
  }, [navigate, canUpdate, canDelete]);

  /* =========================
     VIEW YOKSA
  ========================= */
  if (!isLoading && !canView) {
    return (
      <div className="kuyumcu-main-container">
        <KuyumcuLeftSideNavbar />
        <div className="kuyumcu-page-content">
          <div className="kk-baslik-bar">
            <div className="kk-baslik-sol">
              <div className="kk-baslik-ust">Kuyumcu Yönetimi</div>
              <div className="kk-baslik-alt">Alt Kullanıcılar ve Roller</div>
            </div>
          </div>

          <Box sx={{ mt: 2 }}>
            <Alert severity="warning">Bu sayfayı görüntüleme yetkiniz yok.</Alert>
          </Box>
        </div>
      </div>
    );
  }

  return (
    <div className="kuyumcu-main-container">
      <PremiumBackground />
      <KuyumcuLeftSideNavbar />

      <div className="kuyumcu-page-content">
        {/* Başlık */}
        <div className="kk-baslik-bar">
          <div className="kk-baslik-sol">
            <div className="kk-baslik-ust"><FaUsers/> Kuyumcu Yönetimi</div>
            <div className="kk-baslik-alt">Alt Kullanıcılar ve Roller</div>
          </div>

          <div className="kk-baslik-sag">
            {canCreate && (
              <Button
                className="kk-yeni-btn"
                variant="contained"
                onClick={() => {
                  resetForm();
                  setPopupOpen(true);
                }}
              >
                + Yeni Kullanıcı
              </Button>
            )}
          </div>
        </div>

        {error && (
          <Box sx={{ mt: 1 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <ProsetDataGrid
              rows={rows}
              columns={columns}
              height={560}
              getRowId={(r) => r.id}
            />
          )}
        </Box>

        {/* CREATE / UPDATE POPUP */}
        <ProsetPopup
          open={popupOpen}
          title={editMode ? "Kullanıcı Düzenle" : "Yeni Alt Kullanıcı"}
          editMode={false} // Popup içindeki “Sil” butonunu kullanmıyoruz
          sabitMi={true} // emniyet
          onCancel={() => {
            setPopupOpen(false);
            resetForm();
            setError("");
          }}
          onSave={kaydet}
        >
          <div className="kk-form">
            <TextField
              label="Kullanıcı Adı"
              fullWidth
              value={form.kullaniciAdi}
              onChange={(e) => setForm((p) => ({ ...p, kullaniciAdi: e.target.value }))}
              disabled={editMode} // kullanıcı adı değişmesin
            />

            {/* İSTENEN YENİ ALANLAR (kullanıcı adından hemen sonra) */}
            <TextField
              label="İsim"
              fullWidth
              value={form.isim}
              onChange={(e) => setForm((p) => ({ ...p, isim: e.target.value }))}
            />

            <TextField
              label="Soyisim"
              fullWidth
              value={form.soyisim}
              onChange={(e) => setForm((p) => ({ ...p, soyisim: e.target.value }))}
            />

            <TextField
              label={editMode ? "Yeni Şifre (opsiyonel)" : "Şifre"}
              type="password"
              fullWidth
              value={form.password}
              onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
            />

            <TextField
              label="Mail"
              fullWidth
              value={form.mail}
              onChange={(e) => setForm((p) => ({ ...p, mail: e.target.value }))}
            />

            <TextField
              label="Telefon"
              fullWidth
              value={form.telNo}
              onChange={(e) => setForm((p) => ({ ...p, telNo: e.target.value }))}
            />

            <TextField
              select
              label="Üyelik Tipi"
              fullWidth
              value={form.uyelikTip}
              onChange={(e) => setForm((p) => ({ ...p, uyelikTip: e.target.value }))}
            >
              {uyelikTipSecenekleri.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>

            {editMode && (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.aktifUyeMi}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, aktifUyeMi: e.target.checked }))
                    }
                  />
                }
                label={form.aktifUyeMi ? "Aktif" : "Pasif"}
              />
            )}

            <div className="kk-bilgi-not">
              Not: Alt kullanıcı eklenince lisans geçerlilik tarihi otomatik olarak bugüne
              set edilir.
            </div>
          </div>
        </ProsetPopup>

        {/* SİLME ONAY POPUP */}
        <SilmeUyariPopup
          open={silmePopupOpen}
          title="Silme Onayı"
          message={silmeMesaji}
          onCancel={() => {
            setSilmePopupOpen(false);
            setSilinecekId(null);
          }}
          onConfirm={silOnayla}
        />
      </div>
    </div>
  );
}
